import { useGetFprInspectionDetail } from '@/api/Inspections';
import {
  usePassport,
  usePassportJobs,
  usePassportTransactions,
} from '@/api/Passport';
import { Button, Card, Col, Modal, Row, Spin, Tabs, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { PassportDetails } from './PassportDetails';
import { PassportTransactions } from './PassportTransactions';
import { JobsDataGrid } from './JobsDataGrid';
import { LicensePlate } from '../LicensePlate';
import { faClose, Icon } from '../Icon';
import { FprDetails } from './FprDetails';

interface VehicleInformationProps {
  vin?: string;
  showAsLabel?: boolean;
}
export const VehicleInformation = ({
  vin,
  showAsLabel,
}: VehicleInformationProps) => {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState<string>('');
  const [activeVehicleTab, setActiveVehicleTab] = useState('1');

  const { data: passport, isFetching: isPassportLoading } = usePassport(
    searchValue,
    { enabled: !!searchValue.length },
  );

  const { data: passportJobsData, isFetching: isPassportJobsLoading } =
    usePassportJobs(searchValue, { enabled: !!searchValue.length });

  const {
    data: passportsTransactionsData,
    isFetching: isPassportsTransactionLoading,
  } = usePassportTransactions(searchValue, { enabled: !!searchValue.length });

  const { data: fprInspections } = useGetFprInspectionDetail(searchValue);

  const isLoading =
    isPassportLoading || isPassportJobsLoading || isPassportsTransactionLoading;

  const showReRoute =
    passport?.customerId === 4 &&
    !!passport?.vehicleId &&
    !!passport?.vinStatus &&
    Number(passport?.vinStatus || 0) < 500;

  useEffect(() => {
    setSearchValue(!showAsLabel && vin && vin.length ? vin : '');
  }, [vin, showAsLabel]);

  return vin ? (
    showAsLabel ? (
      <>
        <Tooltip title="Click to view vehicle information">
          <span className="cursor-pointer" onClick={() => setSearchValue(vin)}>
            {vin}
          </span>
        </Tooltip>
        <Modal
          open={!!searchValue}
          centered
          closable={false}
          footer={null}
          title={
            <div className="d-flex align-items-center justify-content-between p-2">
              {passport ? (
                <LicensePlate regNumber={passport?.regNumber} vin={vin} />
              ) : (
                <div className="text-white fw-semibold fs-5">Loading...</div>
              )}
              <span className="fw-semibold fs-5" style={{ color: 'white' }}>
                {passport?.manufactureDate
                  ? `${passport.manufactureDate.split('-')[0]} `
                  : null}
                {passport?.model}
              </span>
              <Button type="text" onClick={() => setSearchValue('')}>
                <Icon icon={faClose} color="white" />
              </Button>
            </div>
          }
          width={{
            xs: '90%',
            sm: '80%',
            md: '76%',
            lg: '70%',
            xl: '65%',
            xxl: '60%',
          }}
          styles={{
            content: {
              padding: 0,
            },
            header: {
              backgroundColor: theme.primaryBg,
            },
            body: {
              maxHeight: 'calc(90vh - 52px)',
              overflowY: 'auto',
            },
          }}
        >
          <Spin spinning={isLoading}>
            <Tabs
              defaultActiveKey="1"
              onChange={setActiveVehicleTab}
              type="card"
              className="vehicle-information-tabs"
              items={[
                {
                  key: '1',
                  label: <b>Vehicle</b>,
                  children: (
                    <PassportDetails
                      passportData={passport}
                      showImagesAndMap
                      imagesActive={!!searchValue && activeVehicleTab === '1'}
                    />
                  ),
                },
                {
                  key: '2',
                  label: <b>Transactions</b>,
                  children: (
                    <PassportTransactions
                      transactions={passport ? passportsTransactionsData : []}
                    />
                  ),
                },
                {
                  key: '3',
                  label: <b>Jobs</b>,
                  children: (
                    <JobsDataGrid jobs={passport ? passportJobsData : []} />
                  ),
                },
                {
                  key: '4',
                  label: <b>FPOR</b>,
                  children: (
                    <FprDetails
                      vehicleId={passport?.vehicleId}
                      showReRoute={showReRoute}
                      lastScan={passport?.lastFporScan}
                      fprInspections={fprInspections || []}
                    />
                  ),
                },
              ]}
            />
          </Spin>
        </Modal>
      </>
    ) : (
      <Spin spinning={isLoading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card className="h-100">
              <PassportDetails passportData={passport} showVinModal />
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card className="w-100">
                  <PassportTransactions
                    transactions={passport ? passportsTransactionsData : []}
                  />
                </Card>
              </Col>
              <Col span={24}>
                <Card className="w-100">
                  <JobsDataGrid jobs={passport ? passportJobsData : []} />
                </Card>
              </Col>
            </Row>
          </Col>
          <Col>
            <Card>
              <FprDetails
                vehicleId={passport?.vehicleId}
                showReRoute={showReRoute}
                lastScan={passport?.lastFporScan}
                fprInspections={fprInspections || []}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    )
  ) : null;
};
